import React, { useState, useEffect } from 'react';
import { formatDateTime } from '../utils/dateUtils';
import { toast } from 'sonner';
import {
  Shield, Key, Download, Bell, CheckCircle, AlertTriangle,
  User, Mail, Smartphone, Globe, Lock, Eye, EyeOff, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

import { authHeaders } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminSecurity() {
  const { token: authToken } = useAuth();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [brevoStatus, setBrevoStatus] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [activeTab, setActiveTab] = useState('compte');   // ← "Mon compte" par défaut

  // ── Mon compte ──
  const [accountForm, setAccountForm] = useState({
    current_password: '', new_password: '', confirm_password: '', new_email: ''
  });
  const [showPwd, setShowPwd] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);

  // ── 2FA OTP ──
  const [setup2FA, setSetup2FA] = useState(null);
  const [otp2FA, setOtp2FA] = useState('');
  const [setting2FA, setSetting2FA] = useState(false);

  // ── Email OTP ──
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);

  useEffect(() => {
    fetchStatus();
    fetchLogs();
    fetchBrevo();
  }, []);

  async function fetchStatus() {
    const res = await fetch(`${BACKEND}/api/admin/security/status`, { headers: authHeaders() });
    const data = await res.json();
    setStatus(data);
  }
  async function fetchLogs() {
    const res = await fetch(`${BACKEND}/api/admin/security/logs?limit=50`, { headers: authHeaders() });
    const data = await res.json();
    setLogs(data.logs || []);
  }
  async function fetchBrevo() {
    const res = await fetch(`${BACKEND}/api/notifications/status`, { headers: authHeaders() });
    const data = await res.json();
    setBrevoStatus(data);
  }

  // ── Changement email / mot de passe ──
  async function updateAccount() {
    if (accountForm.new_password && accountForm.new_password !== accountForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas'); return;
    }
    if (accountForm.new_password && accountForm.new_password.length < 8) {
      toast.error('Mot de passe trop court (8 caractères minimum)'); return;
    }
    setAccountSaving(true);
    const token = authToken;
    try {
      const res = await fetch(`${BACKEND}/api/auth/me/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          current_password: accountForm.current_password,
          new_password: accountForm.new_password || undefined,
          new_email: accountForm.new_email || undefined,
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Compte mis à jour !');
        setAccountForm({ current_password: '', new_password: '', confirm_password: '', new_email: '' });
        fetchStatus();
      } else {
        toast.error(data.detail || 'Erreur lors de la mise à jour');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    }
    setAccountSaving(false);
  }

  // ── 2FA OTP Setup ──
  async function setup2FAUser() {
    setSetting2FA(true);
    const token = authToken;
    let userId = 'admin';
    try { userId = JSON.parse(atob(token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/'))).sub; } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    const res = await fetch(`${BACKEND}/api/admin/security/2fa/setup/${userId}`, { method: 'POST', headers: authHeaders() });
    const data = await res.json();
    setSetup2FA({ ...data, userId });
    setSetting2FA(false);
  }
  async function activate2FA() {
    if (!otp2FA || !setup2FA) return;
    const res = await fetch(`${BACKEND}/api/admin/security/2fa/activate/${setup2FA.userId}`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ code: otp2FA })
    });
    const data = await res.json();
    if (data.success) {
      toast.success('2FA activé ! Reconnectez-vous pour tester.');
      setSetup2FA(null); setOtp2FA(''); fetchStatus();
    } else {
      toast.error('Code OTP invalide');
    }
  }

  // ── Email OTP (envoi code par email) ──
  async function sendEmailOtp() {
    setEmailOtpLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/send-email-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      if (data.success) {
        setEmailOtpSent(true);
        toast.success('Code envoyé sur votre email');
      } else {
        toast.error(data.detail || 'Erreur envoi email');
      }
    } catch (e) { toast.error('Erreur'); }
    setEmailOtpLoading(false);
  }

  // ── Sauvegarde ──
  async function createBackup() {
    setBackupLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/security/backup`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        toast.success(`Sauvegarde créée — ${data.total_documents} docs`);
        setLastBackup(data);
        const dlRes = await fetch(`${BACKEND}/api/admin/security/backup/download/${data.backup_id}`, { headers: authHeaders() });
        if (dlRes.ok) {
          const blob = await dlRes.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `backup_show_evenement_${new Date().toISOString().split('T')[0]}.json`;
          a.click();
        }
      }
    } catch (e) { toast.error('Erreur sauvegarde'); }
    setBackupLoading(false);
  }

  const ACTION_LABELS = {
    '2fa_activate': '2FA activé', '2fa_disable': '2FA désactivé',
    'backup_created': 'Sauvegarde créée', 'account_update': 'Compte modifié',
    'login_2fa_success': 'Connexion 2FA validée'
  };

  const TABS = [
    { id: 'compte',      label: 'Mon compte',      icon: <User size={14} /> },
    { id: '2fa',         label: '2FA Authenticator', icon: <Smartphone size={14} /> },
    { id: 'email_otp',   label: 'Code email',       icon: <Mail size={14} /> },
    { id: 'google_auth', label: 'Google OAuth',     icon: <Globe size={14} /> },
    { id: 'backup',      label: 'Sauvegarde',        icon: <Download size={14} /> },
    { id: 'logs',        label: 'Journaux',          icon: <Shield size={14} /> },
  ];

  const inputStyle = { background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' };

  return (
    <div className="max-w-3xl" data-theme="admin-dark">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Parametres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Securite & 2FA</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Mot de passe, 2FA, sauvegardes</p>
      </div>

      <div className="mb-6">
        <h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>Sécurité & Accès admin</h2>
        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
          Mot de passe · 2FA OTP · Code email · Google OAuth (structure prête)
        </p>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl overflow-x-auto" style={{ background: 'var(--admin-surface)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all"
            style={{ background: activeTab === t.id ? '#D4AF37' : 'transparent', color: activeTab === t.id ? '#0D0D0D' : 'rgba(255,255,255,0.5)' }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════
          MON COMPTE — email + mot de passe
          ════════════════════════════════════════ */}
      {activeTab === 'compte' && (
        <div className="p-5 rounded-xl space-y-4" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.3)' }}>
          <div className="flex items-center gap-2 mb-1">
            <User size={16} style={{ color: 'var(--admin-gold)' }} />
            <h3 className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>Modifier mon compte admin</h3>
          </div>
          <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
            Compte actuel : <span style={{ color: 'var(--admin-text)' }}>admin@show-evenement.fr</span>
          </p>

          {/* Nouvel email */}
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Nouvel email (facultatif)
            </label>
            <input type="email" value={accountForm.new_email}
              onChange={e => setAccountForm(f => ({ ...f, new_email: e.target.value }))}
              placeholder="nouveau@show-evenement.fr"
              className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle} />
          </div>

          {/* Séparateur */}
          <div style={{ borderTop: '1px solid #2A2A2A', paddingTop: '12px' }}>
            <p className="text-xs font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Changement de mot de passe
            </p>

            <div className="space-y-3">
              {/* Mot de passe actuel */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  Mot de passe actuel <span style={{ color: '#FF5C6C' }}>*</span>
                </label>
                <div className="relative">
                  <input type={showPwd ? 'text' : 'password'} value={accountForm.current_password}
                    onChange={e => setAccountForm(f => ({ ...f, current_password: e.target.value }))}
                    placeholder="Votre mot de passe actuel"
                    className="w-full px-3 py-2.5 pr-10 rounded-xl text-sm" style={inputStyle} />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--admin-faint)' }}>
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Nouveau mot de passe */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  Nouveau mot de passe (8 caractères minimum)
                </label>
                <input type="password" value={accountForm.new_password}
                  onChange={e => setAccountForm(f => ({ ...f, new_password: e.target.value }))}
                  placeholder="Nouveau mot de passe sécurisé"
                  className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle} />
              </div>

              {/* Confirmation */}
              {accountForm.new_password && (
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    Confirmer le nouveau mot de passe
                  </label>
                  <input type="password" value={accountForm.confirm_password}
                    onChange={e => setAccountForm(f => ({ ...f, confirm_password: e.target.value }))}
                    placeholder="Confirmer"
                    className="w-full px-3 py-2.5 rounded-xl text-sm"
                    style={{ ...inputStyle, border: `1px solid ${accountForm.confirm_password && accountForm.confirm_password !== accountForm.new_password ? '#FF5C6C' : '#2A2A2A'}` }} />
                  {accountForm.confirm_password && accountForm.confirm_password !== accountForm.new_password && (
                    <p className="text-xs mt-1" style={{ color: '#FF5C6C' }}>Les mots de passe ne correspondent pas</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <button onClick={updateAccount}
            disabled={accountSaving || !accountForm.current_password}
            className="w-full btn-gold py-3 rounded-xl text-sm font-bold disabled:opacity-40">
            {accountSaving ? 'Mise à jour...' : 'Enregistrer les modifications'}
          </button>
        </div>
      )}

      {/* ════════════════════════════════════════
          2FA — Google Authenticator / Authy
          ════════════════════════════════════════ */}
      {activeTab === '2fa' && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Status */}
          {status && (
            <div className="flex items-center gap-3 p-4 rounded-xl"
              style={{ background: 'var(--admin-surface)', border: `1px solid ${status['2fa']?.admins_2fa_enabled > 0 ? 'rgba(61,220,151,0.3)' : 'rgba(255,204,102,0.3)'}` }}>
              {status['2fa']?.admins_2fa_enabled > 0
                ? <CheckCircle size={18} style={{ color: '#3DDC97' }} />
                : <AlertTriangle size={18} style={{ color: '#FFCC66' }} />}
              <div>
                <p className="text-sm font-semibold"
                  style={{ color: status['2fa']?.admins_2fa_enabled > 0 ? '#3DDC97' : '#FFCC66' }}>
                  {status['2fa']?.admins_2fa_enabled > 0 ? '2FA activé ✓' : '2FA non encore activé'}
                </p>
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                  Compatible Google Authenticator et Authy
                </p>
              </div>
            </div>
          )}

          <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
            <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--admin-gold)' }}>
              <Smartphone size={15} className="inline mr-1" /> Configuration 2FA (TOTP)
            </h3>
            <p className="text-xs mb-4" style={{ color: 'var(--admin-muted)', lineHeight: '1.6' }}>
              Chaque connexion admin demandera un code à 6 chiffres renouvelé toutes les 30 secondes.
            </p>

            {!setup2FA ? (
              <button onClick={setup2FAUser} disabled={setting2FA}
                className="btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40">
                {setting2FA ? 'Génération...' : 'Configurer 2FA pour mon compte'}
              </button>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  1. Ouvrez Google Authenticator ou Authy sur votre téléphone<br />
                  2. Scannez ce QR code :
                </p>
                <img src={setup2FA.qr_code} alt="QR 2FA"
                  className="w-48 h-48 rounded-xl mx-auto"
                  style={{ background: '#FFFFFF', padding: '8px' }} loading="lazy" />
                <p className="text-xs text-center" style={{ color: 'var(--admin-muted)' }}>
                  Clé manuelle : <span className="font-mono-ose" style={{ color: '#CFCFCF' }}>{setup2FA.secret}</span>
                </p>
                <div className="flex gap-3">
                  <input type="text" value={otp2FA} onChange={e => setOtp2FA(e.target.value.replace(/\D/g,'').slice(0,6))}
                    placeholder="Code à 6 chiffres" maxLength={6} inputMode="numeric"
                    className="flex-1 px-3 py-2.5 rounded-xl text-sm font-mono-ose text-center tracking-widest"
                    style={inputStyle} />
                  <button onClick={activate2FA} disabled={otp2FA.length !== 6}
                    className="btn-gold px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40">
                    Activer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          CODE EMAIL — OTP par email
          ════════════════════════════════════════ */}
      {activeTab === 'email_otp' && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
            <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--admin-gold)' }}>
              <Mail size={15} className="inline mr-1" /> Validation par code email
            </h3>
            <p className="text-xs mb-4" style={{ color: 'var(--admin-muted)', lineHeight: '1.6' }}>
              Un code à 6 chiffres est envoyé sur votre adresse email à chaque connexion admin.
              Nécessite que Brevo soit configuré.
            </p>

            {/* Status Brevo */}
            <div className="flex items-center gap-2 p-3 rounded-xl mb-4"
              style={{ background: brevoStatus?.brevo_enabled ? 'rgba(61,220,151,0.08)' : 'rgba(255,204,102,0.08)', border: `1px solid ${brevoStatus?.brevo_enabled ? 'rgba(61,220,151,0.2)' : 'rgba(255,204,102,0.2)'}` }}>
              {brevoStatus?.brevo_enabled
                ? <CheckCircle size={14} style={{ color: '#3DDC97' }} />
                : <AlertTriangle size={14} style={{ color: '#FFCC66' }} />}
              <p className="text-xs" style={{ color: brevoStatus?.brevo_enabled ? '#3DDC97' : '#FFCC66' }}>
                {brevoStatus?.brevo_enabled ? 'Brevo actif — envoi email possible' : 'Brevo non configuré — activez Brevo d\'abord'}
              </p>
            </div>

            {/* Test envoi */}
            {!emailOtpSent ? (
              <button onClick={sendEmailOtp} disabled={emailOtpLoading || !brevoStatus?.brevo_enabled}
                className="btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40 w-full">
                {emailOtpLoading ? 'Envoi...' : 'Envoyer un code test par email'}
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-xs" style={{ color: '#3DDC97' }}>
                  ✓ Code envoyé. Vérifiez votre boîte email.
                </p>
                <div className="flex gap-3">
                  <input type="text" value={emailOtpCode}
                    onChange={e => setEmailOtpCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                    placeholder="Code reçu par email" maxLength={6} inputMode="numeric"
                    className="flex-1 px-3 py-2.5 rounded-xl text-sm font-mono-ose text-center tracking-widest"
                    style={inputStyle} />
                  <button onClick={() => toast.success('Validation email OK — structure prête')}
                    disabled={emailOtpCode.length !== 6}
                    className="btn-gold px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40">
                    Valider
                  </button>
                </div>
                <button onClick={() => setEmailOtpSent(false)}
                  className="text-xs" style={{ color: 'var(--admin-faint)' }}>
                  Renvoyer un code
                </button>
              </div>
            )}

            <div className="mt-4 p-3 rounded-xl" style={{ background: 'var(--admin-gold-dim)', border: '1px solid rgba(212,175,55,0.15)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--admin-gold)' }}>
                Activation intégration complète
              </p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)', lineHeight: '1.6' }}>
                Pour activer la vérification email obligatoire à chaque connexion, combinez avec le 2FA OTP existant. Structure complète prête — dites-le et je l'active.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          GOOGLE OAUTH — Structure préparée
          ════════════════════════════════════════ */}
      {activeTab === 'google_auth' && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
            <h3 className="font-semibold text-sm mb-3" style={{ color: 'var(--admin-gold)' }}>
              <Globe size={15} className="inline mr-1" /> Connexion Google OAuth
            </h3>

            <div className="p-3 rounded-xl mb-4"
              style={{ background: 'var(--admin-teal-dim)', border: '1px solid var(--admin-teal-line)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--admin-teal)' }}>
                Structure architecturale prête
              </p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)', lineHeight: '1.6' }}>
                Le backend supporte l'ajout d'OAuth Google. L'activation nécessite un projet Google Cloud et un Client ID.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Pour activer Google OAuth :
              </p>
              {[
                '1. Aller sur console.cloud.google.com',
                '2. Créer un projet "Show Événement"',
                '3. APIs & Services → Identifiants → Créer un ID client OAuth 2.0',
                '4. URI de redirection : https://votre-domaine.fr/api/auth/google/callback',
                '5. Copier CLIENT_ID et CLIENT_SECRET',
                '6. Me les transmettre — j\'active en 5 minutes'
              ].map((s, i) => (
                <div key={`list-${i}`} className="flex items-start gap-2">
                  <span className="w-4 h-4 rounded-full flex items-center justify-center text-xs flex-shrink-0"
                    style={{ background: '#2A2A2A', color: 'var(--admin-muted)' }}>
                    {i + 1}
                  </span>
                  <p className="text-xs" style={{ color: 'var(--admin-muted)', lineHeight: '1.5' }}>{s}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 p-3 rounded-xl" style={{ background: 'rgba(255,204,102,0.06)', border: '1px solid rgba(255,204,102,0.15)' }}>
              <p className="text-xs" style={{ color: '#FFCC66' }}>
                ⚡ SMS/Appel : structure prête également — nécessite Twilio ou Brevo SMS. Transmettez la clé pour activer.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════
          SAUVEGARDE
          ════════════════════════════════════════ */}
      {activeTab === 'backup' && (
        <div className="p-5 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
          <div className="flex items-center gap-3 mb-4">
            <Download size={18} style={{ color: '#3DDC97' }} />
            <h3 className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>Sauvegarde base de données</h3>
          </div>
          <p className="text-xs mb-4" style={{ color: 'var(--admin-muted)' }}>
            Export JSON de toutes les collections (73 produits, devis, clients, commandes, paiements…)
          </p>
          {lastBackup && (
            <div className="p-3 rounded-xl mb-4"
              style={{ background: 'rgba(61,220,151,0.08)', border: '1px solid rgba(61,220,151,0.2)' }}>
              <p className="text-xs font-semibold" style={{ color: '#3DDC97' }}>Dernière sauvegarde</p>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                {lastBackup.total_documents} documents — {(lastBackup.file_size_bytes / 1024).toFixed(1)} KB
              </p>
            </div>
          )}
          <button onClick={createBackup} disabled={backupLoading}
            className="btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            <Download size={16} />
            {backupLoading ? 'Sauvegarde en cours...' : 'Créer + Télécharger la sauvegarde'}
          </button>
          <p className="text-xs mt-3" style={{ color: 'var(--admin-faint)' }}>
            Recommandé : une sauvegarde hebdomadaire avant toute mise à jour.
          </p>
        </div>
      )}

      {/* ════════════════════════════════════════
          JOURNAUX
          ════════════════════════════════════════ */}
      {activeTab === 'logs' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>{logs.length} entrées</p>
            <button onClick={fetchLogs} className="p-2 rounded-lg"
              style={{ background: 'var(--admin-surface)', color: 'var(--admin-gold)' }}>
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #2A2A2A' }}>
            {logs.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Aucun journal pour l'instant</p>
              </div>
            ) : logs.map((log, i) => (
              <div key={log.id} className="flex items-center gap-3 px-4 py-2.5"
                style={{ background: i % 2 === 0 ? '#161616' : '#121212', borderBottom: '1px solid rgba(42,42,42,0.5)' }}>
                <div className="flex-1">
                  <span className="px-2 py-0.5 rounded-full text-xs mr-2"
                    style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--admin-gold)' }}>
                    {ACTION_LABELS[log.action] || log.action}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>{log.details}</span>
                </div>
                <span className="text-xs font-mono-ose" style={{ color: 'var(--admin-faint)' }}>
                  {log.timestamp ? formatDateTime(log.timestamp) : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
